import { DecimalField } from './DecimalField.tsx';
import { TextField } from './TextField.tsx';
import type { Currency } from '../../shared/invoice.ts';
import { computeLineAmountCents, formatCurrencyCents } from '../../shared/format.ts';

export interface InvoiceEditorItem {
  description: string;
  quantity: string;
  rate: string;
}

export interface InvoiceItemsTableProps {
  currency: Currency;
  items: InvoiceEditorItem[];
  readOnly?: boolean;
  onChange: (items: InvoiceEditorItem[]) => void;
}

export function InvoiceItemsTable({
  currency,
  items,
  readOnly,
  onChange,
}: InvoiceItemsTableProps): React.ReactElement {
  function updateItem(index: number, patch: Partial<InvoiceEditorItem>): void {
    onChange(items.map((item, itemIndex) => (itemIndex === index ? { ...item, ...patch } : item)));
  }

  function addItem(): void {
    onChange([...items, { description: '', quantity: '1', rate: '1' }]);
  }

  function deleteItem(index: number): void {
    if (items.length <= 1) return;
    onChange(items.filter((_item, itemIndex) => itemIndex !== index));
  }

  return (
    <section className="space-y-4">
      <div className="flex items-center justify-between gap-3">
        <h2 className="text-[11px] font-semibold uppercase tracking-[0.22em] text-ink-muted">
          Items
        </h2>
        <button
          type="button"
          disabled={readOnly}
          onClick={addItem}
          className="inline-flex min-h-11 items-center justify-center rounded-full border border-line bg-paper px-4 py-1.5 text-xs font-semibold uppercase tracking-[0.08em] text-ink transition hover:border-ochre/40 hover:bg-paper-deep disabled:cursor-not-allowed disabled:opacity-60"
        >
          + Line item
        </button>
      </div>

      <div className="space-y-3">
        <div className="hidden grid-cols-[minmax(16rem,1fr)_7rem_8rem_8rem_3rem] gap-2 border-b border-line px-4 py-3 text-[10px] font-semibold uppercase tracking-[0.22em] text-ink-muted md:grid">
          <span>Item</span>
          <span>Quantity</span>
          <span>Rate</span>
          <span className="text-right">Amount</span>
          <span />
        </div>
        {items.map((item, index) => {
          const rowNumber = index + 1;
          const amount = formatCurrencyCents(safeLineAmountCents(item), currency);
          return (
            <article
              key={index}
              data-line-item-card
              className="space-y-3 rounded-2xl border border-line bg-paper-soft p-4 md:grid md:grid-cols-[minmax(16rem,1fr)_7rem_8rem_8rem_3rem] md:items-start md:gap-2 md:space-y-0 md:border-0 md:bg-transparent md:p-0"
            >
              <TextField
                id={`invoice-item-${rowNumber}-description`}
                label={`Item ${rowNumber} description`}
                labelClassName="sr-only"
                containerClassName="space-y-0 md:min-w-0"
                disabled={readOnly}
                value={item.description}
                onChange={(event) => updateItem(index, { description: event.currentTarget.value })}
              />
              <div className="grid grid-cols-2 gap-2 md:contents">
                <DecimalField
                  id={`invoice-item-${rowNumber}-quantity`}
                  label={`Item ${rowNumber} quantity`}
                  labelClassName="sr-only"
                  containerClassName="space-y-0"
                  disabled={readOnly}
                  value={item.quantity}
                  onChange={(event) => updateItem(index, { quantity: event.currentTarget.value })}
                />
                <DecimalField
                  id={`invoice-item-${rowNumber}-rate`}
                  label={`Item ${rowNumber} rate`}
                  labelClassName="sr-only"
                  containerClassName="space-y-0"
                  disabled={readOnly}
                  value={item.rate}
                  onChange={(event) => updateItem(index, { rate: event.currentTarget.value })}
                />
              </div>
              <div className="grid grid-cols-[minmax(0,1fr)_2.75rem] gap-2 md:contents">
                <p className="flex min-h-11 min-w-0 items-center justify-end break-words rounded-lg border border-line bg-paper-deep/50 px-3 text-right font-serif text-base tabular-nums text-ink">
                  {amount}
                </p>
                <button
                  type="button"
                  aria-label={`Delete item ${rowNumber}`}
                  disabled={readOnly || items.length <= 1}
                  onClick={() => deleteItem(index)}
                  className="inline-flex h-11 w-11 items-center justify-center rounded-full border border-line bg-paper text-xl leading-none text-ink-muted transition hover:border-clay/40 hover:bg-clay/10 hover:text-clay disabled:cursor-not-allowed disabled:opacity-60"
                >
                  <span aria-hidden="true">×</span>
                </button>
              </div>
            </article>
          );
        })}
      </div>
    </section>
  );
}

function safeLineAmountCents(item: InvoiceEditorItem): number {
  try {
    return computeLineAmountCents(item);
  } catch {
    return 0;
  }
}
